import React, { useState } from 'react';
import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';

const SmallEstateAffidavitPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    situation: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      await fetch('https://readdy.ai/api/form/d67b5hea728k8ctu1q1g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        propertyAddress: '',
        situation: '',
        message: '',
      });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Small Estate Affidavit Texas | Skip Probate for Estates Under $75,000 | El Paso"
        description="Complete guide to Small Estate Affidavits in Texas. Learn eligibility requirements, the filing process, costs, and how to transfer property without probate for estates valued under $75,000 in El Paso."
        keywords="small estate affidavit Texas, skip probate Texas, estate under 75000, transfer property without probate, El Paso estate planning, Texas estate laws"
        canonical="/elpaso-tx/small-estate-affidavit"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'Small Estate Affidavit in Texas: Complete Guide to Skipping Probate',
          description:
            'Comprehensive guide to using a Small Estate Affidavit in Texas to transfer property without probate for estates valued under $75,000.',
          author: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
          publisher: {
            '@type': 'Organization',
            name: 'TREX RE LLC',
          },
        }}
      />
      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b04ed90e86a9f52736900a9f0760bf24.jpg"
            alt="Small Estate Affidavit Documents"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/75 to-gray-900/60"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full mb-6">
                Estate Planning Guide
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Small Estate Affidavit in Texas:{' '}
                <span className="text-teal-400">Skip Probate Legally</span>
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                Learn how Texas families can transfer property and assets without
                expensive probate court proceedings using a Small Estate
                Affidavit for estates valued under $75,000.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="tel:+1-915-206-5814"
                  className="inline-flex items-center px-6 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  Call (915) 206-5814
                </a>
                <a
                  href="/get-cash-offer"
                  className="inline-flex items-center px-6 py-4 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Get Cash Offer
                  <i className="ri-arrow-right-line ml-2"></i>
                </a>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Need Help With a Small Estate?
              </h3>
              <p className="text-gray-600 mb-6">Get expert guidance on your options</p>

              {submitSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-teal-600"></i>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">
                    Thank You!
                  </h4>
                  <p className="text-gray-600">
                    We&apos;ll contact you within 24 hours.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  id="small-estate-hero-form"
                  className="space-y-4"
                >
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="text"
                    name="propertyAddress"
                    placeholder="Property Address (if applicable)"
                    value={formData.propertyAddress}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                  >
                    {isSubmitting ? 'Submitting...' : 'Get Free Consultation'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              When a loved one passes away, the last thing grieving families
              want to deal with is a lengthy, expensive probate process.
              Fortunately, Texas law provides a streamlined alternative for
              smaller estates: the <strong>Small Estate Affidavit</strong>.
              This legal document allows heirs to collect and distribute assets
              without going through formal probate court proceedings,
              saving both time and money during an already difficult period.
            </p>

            <p className="text-gray-700 leading-relaxed mb-8">
              For many El Paso families, understanding when and how to use a
              Small Estate Affidavit can mean the difference between a simple,
              straightforward asset transfer and months of court appearances,
              attorney fees, and administrative headaches. In this comprehensive
              guide, we will walk you through everything you need to know about
              Small Estate Affidavits in Texas, including eligibility
              requirements, the step-by-step process, costs involved, and
              important limitations to be aware of.
            </p>

            <p className="text-gray-700 leading-relaxed">
              Whether you are dealing with an{' '}
              <a
                href="/inherited-homes"
                className="text-teal-600 hover:text-teal-700 font-semibold"
              >
                inherited property situation
              </a>{' '}
              or simply trying to understand your options for estate planning,
              this guide will provide the clarity you need to make informed
              decisions about your family&apos;s future.
            </p>
          </div>
        </div>
      </section>

      {/* What Is a Small Estate Affidavit Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What Exactly Is a{' '}
                <span className="text-teal-600">Small Estate Affidavit</span>?
              </h2>

              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  A Small Estate Affidavit is a sworn legal document that
                  allows the heirs of a deceased person to claim and distribute
                  the decedent&apos;s assets without going through the formal
                  probate process. In Texas, this procedure is governed by
                  Section 205 of the Texas Estates Code and is specifically
                  designed for estates that meet certain value thresholds and
                  other qualifying criteria.
                </p>

                <p className="text-gray-700 leading-relaxed mb-6">
                  Think of it as a simplified, expedited alternative to
                  traditional probate. Instead of filing a probate case,
                  attending court hearings, and waiting months for a judge to
                  approve asset distributions, qualifying heirs can simply
                  prepare and file a Small Estate Affidavit, have it approved by
                  the court, and then use it to collect assets from banks,
                  transfer vehicle titles, and handle other estate matters.
                </p>

                <p className="text-gray-700 leading-relaxed">
                  The Small Estate Affidavit must be signed by all
                  distributees (people entitled to inherit) and two
                  disinterested witnesses who have personal knowledge of the
                  family history and circumstances. Once filed with the
                  appropriate county court and approved by a judge, it becomes a
                  legally binding document that third parties like banks and
                  title companies must honor.
                </p>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=legal%20document%20small%20estate%20affidavit%20form%20with%20notary%20seal%20and%20signatures%20family%20tree%20diagram%20showing%20inheritance%20lines%20calculator%20and%20financial%20statements%20on%20clean%20wooden%20desk%20warm%20natural%20lighting%20professional%20legal%20photography&width=700&height=500&seq=small-estate-doc-002&orientation=landscape"
                alt="Small Estate Affidavit Document"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility Requirements Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Eligibility Requirements:{' '}
              <span className="text-teal-600">Does Your Estate Qualify?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Not every estate can use a Small Estate Affidavit. Texas law sets
              specific criteria that must be met.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Estate Value Limit
              </h3>
              <p className="text-gray-600 leading-relaxed">
                The total value of the estate&apos;s assets (excluding
                homestead and exempt property) must not exceed{' '}
                <strong>$75,000</strong>. This includes bank accounts,
                vehicles, personal property, and any other non-exempt assets.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-time-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                30-Day Waiting Period
              </h3>
              <p className="text-gray-600 leading-relaxed">
                At least <strong>30 days</strong> must have passed since the
                decedent&apos;s death before filing the affidavit. This waiting
                period allows time for any potential creditors or other
                interested parties to come forward.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-file-forbid-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                No Pending Probate
              </h3>
              <p className="text-gray-600 leading-relaxed">
                No petition for the appointment of a personal representative
                (executor or administrator) can be pending or have been
                granted. If probate has already been initiated, you cannot use
                this alternative.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-scales-3-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Assets Exceed Debts
              </h3>
              <p className="text-gray-600 leading-relaxed">
                The value of the estate&apos;s assets (excluding homestead and
                exempt property) must be sufficient to pay all known debts and
                liabilities. The estate cannot be insolvent.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-home-heart-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Real Property Limitations
              </h3>
              <p className="text-gray-600 leading-relaxed">
                The only real property in the estate must be the decedent&apos;s
                homestead. If the deceased owned other real estate (rental
                properties, vacant land, etc.), a Small Estate Affidavit
                typically cannot be used.
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-group-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                All Heirs Must Sign
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Every person entitled to inherit (distributee) must sign the
                affidavit. If any heir refuses to sign or cannot be located,
                the Small Estate Affidavit process cannot be completed.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-6">
            <div className="flex items-start">
              <i className="ri-error-warning-line text-2xl text-amber-600 mr-4 mt-1"></i>
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">
                  Important Note About Real Estate
                </h4>
                <p className="text-gray-700">
                  While a Small Estate Affidavit can help transfer the homestead
                  property, it has significant limitations for real estate.
                  Many title companies are hesitant to insure titles transferred
                  solely through a Small Estate Affidavit. If you need to sell
                  inherited property, you may want to explore other options like
                  an{' '}
                  <a
                    href="/elpaso-tx/affidavit-of-heirship"
                    className="text-teal-600 hover:text-teal-700 font-semibold"
                  >
                    Affidavit of Heirship
                  </a>{' '}
                  or formal probate.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Step-by-Step Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              The Step-by-Step Process:{' '}
              <span className="text-teal-600">How to File</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Filing a Small Estate Affidavit involves several important steps.
              Here&apos;s what you need to know.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-6">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Wait 30 Days After Death
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Texas law requires a mandatory 30-day waiting period after
                    the decedent&apos;s death before you can file a Small Estate
                    Affidavit. Use this time to gather necessary documents and
                    information about the estate&apos;s assets and debts.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-6">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Prepare the Affidavit
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    The affidavit must include specific information about the
                    deceased, all heirs, estate assets, and debts. You can find
                    templates online or work with an attorney to ensure all
                    required information is included correctly. The document must
                    be precise and complete.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-6">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Gather All Signatures
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Every person entitled to inherit must sign the affidavit in
                    front of a notary public. Additionally, you need two
                    disinterested witnesses (people who are not heirs and have no
                    financial interest in the estate) who have personal knowledge
                    of the family circumstances to sign as well.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-6">
                  4
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    File with the Court
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    File the completed and notarized affidavit with the probate
                    court (or county court at law) in the county where the
                    deceased person lived. You&apos;ll need to pay a filing fee,
                    which varies by county but is typically between $200-$400.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-6">
                  5
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Court Approval
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    A judge will review the affidavit to ensure it meets all
                    legal requirements. If everything is in order, the judge will
                    approve it and the court will file it as an official record.
                    This typically happens within a few days to a few weeks,
                    depending on the court&apos;s schedule.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-lg mr-6">
                  6
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Use the Affidavit to Collect Assets
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    Once approved, you can present certified copies of the filed
                    affidavit to banks, the DMV, and other institutions to
                    collect and transfer the deceased person&apos;s assets. Most
                    entities are required by law to honor a properly filed Small
                    Estate Affidavit.
                  </p>
                </div>
              </div>
            </div>

            <div className="sticky top-8">
              <img
                src="https://readdy.ai/api/search-image?query=family%20members%20meeting%20with%20attorney%20in%20professional%20office%20reviewing%20legal%20documents%20and%20signing%20papers%20together%20warm%20natural%20lighting%20showing%20collaboration%20and%20estate%20planning%20process%20professional%20photography&width=700&height=600&seq=small-estate-process-003&orientation=portrait"
                alt="Family Meeting with Attorney"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Information Required Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Information Is{' '}
              <span className="text-teal-600">Required</span>?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              To complete a Small Estate Affidavit, you&apos;ll need to provide
              detailed information in four key categories.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-user-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                About the Deceased
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>Full legal name and any aliases used</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>Date and place of death</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>Last known address and county of residence</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>Whether they died with or without a will</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>Marital status at time of death</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-group-2-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                About the Heirs
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>Names and addresses of all heirs/distributees</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>Relationship of each heir to the deceased</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>
                    Percentage or share each heir is entitled to receive
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>
                    Whether any heirs are minors (under 18) or incapacitated
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-bank-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                About the Assets
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>
                    Complete list of all estate assets and their values
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>
                    Description of any homestead property (address, legal
                    description)
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>Bank account numbers and financial institutions</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>Vehicle information (make, model, VIN)</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>Personal property and estimated values</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-xl p-8">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-file-list-3-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                About Debts and Liabilities
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>List of all known debts and creditors</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>Outstanding medical bills and funeral expenses</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>Credit card balances and personal loans</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-teal-600 mr-3 mt-1"></i>
                  <span>
                    Statement that assets exceed liabilities (estate is solvent)
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Comparison Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Cost Comparison:{' '}
              <span className="text-teal-600">
                Small Estate Affidavit vs. Probate
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              One of the biggest advantages of a Small Estate Affidavit is the
              significant cost savings compared to formal probate.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-900 text-white">
                    <th className="px-6 py-4 text-left font-semibold">
                      Expense Category
                    </th>
                    <th className="px-6 py-4 text-left font-semibold">
                      Small Estate Affidavit
                    </th>
                    <th className="px-6 py-4 text-left font-semibold">
                      Formal Probate
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      Court Filing Fees
                    </td>
                    <td className="px-6 py-4 text-gray-600">$200 - $400</td>
                    <td className="px-6 py-4 text-gray-600">$300 - $500</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      Attorney Fees
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      $500 - $1,500 (optional)
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      $3,000 - $10,000+
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      Publication Costs
                    </td>
                    <td className="px-6 py-4 text-gray-600">None required</td>
                    <td className="px-6 py-4 text-gray-600">$200 - $400</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      Bond Fees
                    </td>
                    <td className="px-6 py-4 text-gray-600">None required</td>
                    <td className="px-6 py-4 text-gray-600">
                      $500 - $2,000+ (if required)
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      Appraisal Fees
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      Minimal (self-valuation)
                    </td>
                    <td className="px-6 py-4 text-gray-600">$300 - $1,000+</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-gray-900">
                      Timeline
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      30-60 days after death
                    </td>
                    <td className="px-6 py-4 text-gray-600">6-12+ months</td>
                  </tr>
                  <tr className="bg-teal-50">
                    <td className="px-6 py-4 font-bold text-gray-900">
                      Total Estimated Cost
                    </td>
                    <td className="px-6 py-4 font-bold text-teal-600">
                      $700 - $1,900
                    </td>
                    <td className="px-6 py-4 font-bold text-gray-900">
                      $4,300 - $14,000+
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-8 bg-teal-50 border-l-4 border-teal-500 rounded-r-xl p-6">
            <div className="flex items-start">
              <i className="ri-lightbulb-line text-2xl text-teal-600 mr-4 mt-1"></i>
              <div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">
                  Potential Savings
                </h4>
                <p className="text-gray-700">
                  By using a Small Estate Affidavit instead of formal probate,
                  Texas families can save an average of{' '}
                  <strong>$3,600 to $12,000</strong> in legal fees and court
                  costs, while also reducing the time required from months to
                  just weeks.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Limitations Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Important Limitations{' '}
              <span className="text-teal-600">to Consider</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              While Small Estate Affidavits offer many benefits, they also have
              significant limitations you should understand.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=concerned%20family%20discussing%20estate%20limitations%20at%20kitchen%20table%20with%20legal%20documents%20calculator%20and%20property%20papers%20showing%20thoughtful%20consideration%20warm%20home%20interior%20natural%20lighting%20professional%20photography&width=700&height=500&seq=small-estate-limits-004&orientation=landscape"
                alt="Family Discussing Estate Limitations"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>

            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-start">
                  <i className="ri-error-warning-line text-2xl text-amber-600 mr-4 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Title Insurance Issues
                    </h3>
                    <p className="text-gray-600">
                      Many title insurance companies are reluctant to insure
                      property titles transferred via Small Estate Affidavit,
                      which can make selling the property difficult or
                      impossible without additional legal proceedings.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-start">
                  <i className="ri-error-warning-line text-2xl text-amber-600 mr-4 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Limited Real Estate Use
                    </h3>
                    <p className="text-gray-600">
                      The affidavit can only be used for the homestead property.
                      If the deceased owned rental properties, commercial real
                      estate, or vacant land, you&apos;ll need to go through
                      formal probate or use alternative methods.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-start">
                  <i className="ri-error-warning-line text-2xl text-amber-600 mr-4 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      All Heirs Must Cooperate
                    </h3>
                    <p className="text-gray-600">
                      If even one heir refuses to sign or cannot be located, the
                      entire process fails. Family disputes or disagreements can
                      completely derail a Small Estate Affidavit, forcing you
                      into formal probate anyway.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-start">
                  <i className="ri-error-warning-line text-2xl text-amber-600 mr-4 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      No Executor Authority
                    </h3>
                    <p className="text-gray-600">
                      Unlike probate, a Small Estate Affidavit doesn&apos;t
                      appoint an executor with legal authority to act on behalf
                      of the estate. This can create complications when dealing
                      with certain institutions or legal matters.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-start">
                  <i className="ri-error-warning-line text-2xl text-amber-600 mr-4 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Creditor Protection Concerns
                    </h3>
                    <p className="text-gray-600">
                      The affidavit process doesn&apos;t provide the same level
                      of creditor protection as formal probate. Unknown creditors
                      may still make claims against the estate or heirs even
                      after assets are distributed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alternatives Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Alternatives{' '}
              <span className="text-teal-600">to Consider</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              If a Small Estate Affidavit doesn&apos;t fit your situation,
              there are other options available.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-file-text-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Affidavit of Heirship
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                An{' '}
                <a
                  href="/elpaso-tx/affidavit-of-heirship"
                  className="text-teal-600 hover:text-teal-700 font-semibold"
                >
                  Affidavit of Heirship
                </a>{' '}
                is specifically designed for transferring real estate when
                someone dies without a will. Unlike a Small Estate Affidavit, it
                has no value limit and is more widely accepted by title
                companies for property sales.
              </p>
              <p className="text-gray-600 leading-relaxed">
                <strong>Best for:</strong> Inherited real estate that needs to
                be sold, especially when the estate exceeds $75,000 or includes
                multiple properties.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-scales-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Formal Probate
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Traditional probate provides the most comprehensive legal
                protection and authority. Learn more about{' '}
                <a
                  href="/elpaso-tx/texas-probate-guide"
                  className="text-teal-600 hover:text-teal-700 font-semibold"
                >
                  how Texas probate works
                </a>{' '}
                and when it&apos;s the best option for your family.
              </p>
              <p className="text-gray-600 leading-relaxed">
                <strong>Best for:</strong> Estates over $75,000, complex asset
                situations, disputed estates, or when you need full executor
                authority.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Sell to a Cash Buyer
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                If you&apos;re facing{' '}
                <a
                  href="/avoid-foreclosure"
                  className="text-teal-600 hover:text-teal-700 font-semibold"
                >
                  foreclosure on inherited property
                </a>{' '}
                or simply want to avoid the legal complexities altogether, selling
                to a cash buyer can be the fastest solution. We can often
                purchase properties even with title complications.
              </p>
              <p className="text-gray-600 leading-relaxed">
                <strong>Best for:</strong> Quick sales, properties with title
                issues, or when heirs want to avoid legal proceedings entirely.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-file-shield-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Muniment of Title
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                If there&apos;s a valid will and no debts (except secured by
                real estate), Muniment of Title is a simplified probate process
                that&apos;s faster and less expensive than full probate. It
                provides better title insurance acceptance than a Small Estate
                Affidavit.
              </p>
              <p className="text-gray-600 leading-relaxed">
                <strong>Best for:</strong> Estates with a will, minimal debts,
                and real estate that needs clear title for sale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Common Mistakes{' '}
              <span className="text-teal-600">to Avoid</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Don&apos;t let these common errors derail your Small Estate
              Affidavit process.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-red-50 rounded-xl p-8 border-l-4 border-red-500">
              <div className="flex items-start mb-4">
                <i className="ri-close-circle-line text-2xl text-red-600 mr-3 mt-1"></i>
                <h3 className="text-xl font-bold text-gray-900">
                  Miscalculating Estate Value
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Failing to properly value assets or forgetting to exclude
                homestead and exempt property can lead to using this process
                when you shouldn&apos;t, potentially creating legal problems
                later.
              </p>
            </div>

            <div className="bg-red-50 rounded-xl p-8 border-l-4 border-red-500">
              <div className="flex items-start mb-4">
                <i className="ri-close-circle-line text-2xl text-red-600 mr-3 mt-1"></i>
                <h3 className="text-xl font-bold text-gray-900">
                  Filing Too Soon
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Filing before the mandatory 30-day waiting period has passed
                will result in the court rejecting your affidavit, wasting time
                and filing fees.
              </p>
            </div>

            <div className="bg-red-50 rounded-xl p-8 border-l-4 border-red-500">
              <div className="flex items-start mb-4">
                <i className="ri-close-circle-line text-2xl text-red-600 mr-3 mt-1"></i>
                <h3 className="text-xl font-bold text-gray-900">
                  Incomplete Information
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Missing required information about heirs, assets, or debts can
                cause delays or rejection. Double-check that every section is
                complete and accurate before filing.
              </p>
            </div>

            <div className="bg-red-50 rounded-xl p-8 border-l-4 border-red-500">
              <div className="flex items-start mb-4">
                <i className="ri-close-circle-line text-2xl text-red-600 mr-3 mt-1"></i>
                <h3 className="text-xl font-bold text-gray-900">
                  Wrong Witnesses
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Using witnesses who are heirs or have a financial interest in
                the estate invalidates the affidavit. Witnesses must be
                disinterested parties with personal knowledge of the family.
              </p>
            </div>

            <div className="bg-red-50 rounded-xl p-8 border-l-4 border-red-500">
              <div className="flex items-start mb-4">
                <i className="ri-close-circle-line text-2xl text-red-600 mr-3 mt-1"></i>
                <h3 className="text-xl font-bold text-gray-900">
                  Ignoring Debts
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Failing to properly account for and pay estate debts before
                distributing assets can expose heirs to personal liability for
                the deceased&apos;s obligations.
              </p>
            </div>

            <div className="bg-red-50 rounded-xl p-8 border-l-4 border-red-500">
              <div className="flex items-start mb-4">
                <i className="ri-close-circle-line text-2xl text-red-600 mr-3 mt-1"></i>
                <h3 className="text-xl font-bold text-gray-900">
                  Not Considering Alternatives
                </h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Sometimes other options like an Affidavit of Heirship or
                Muniment of Title are better suited to your situation,
                especially if you plan to sell real estate.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked{' '}
              <span className="text-teal-600">Questions</span>
            </h2>
            <p className="text-xl text-gray-600">
              Get answers to common questions about Small Estate Affidavits in
              Texas.
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Can I use a Small Estate Affidavit if there&apos;s a will?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Yes, you can use a Small Estate Affidavit whether the deceased
                died with or without a will, as long as all other eligibility
                requirements are met. However, if the will names an executor who
                wants to serve, formal probate may be more appropriate. The
                affidavit must follow the distribution outlined in the will (if
                one exists) or{' '}
                <a
                  href="/elpaso-tx/texas-intestacy-laws"
                  className="text-teal-600 hover:text-teal-700 font-semibold"
                >
                  Texas intestacy laws
                </a>{' '}
                (if there&apos;s no will).
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                What happens if we discover assets after filing the affidavit?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                If you discover additional assets after filing that push the
                estate value over $75,000, you may need to open a formal probate
                case. It&apos;s crucial to conduct a thorough search for all
                assets before filing. Check bank statements, safe deposit boxes,
                tax returns, and contact former employers about retirement
                accounts.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Do I need an attorney to file a Small Estate Affidavit?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                While it&apos;s not legally required to have an attorney, it&apos;s
                often advisable, especially if you&apos;re unfamiliar with legal
                documents or if there are any complications. An attorney can
                ensure the affidavit is properly prepared and filed, potentially
                saving you time and preventing costly mistakes. Many attorneys
                offer flat-fee services for Small Estate Affidavits ranging from
                $500 to $1,500.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Can I sell inherited property using a Small Estate Affidavit?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                This is one of the biggest challenges with Small Estate
                Affidavits. While legally you can transfer property ownership
                using one, many title insurance companies won&apos;t insure the
                title, making it very difficult to sell to traditional buyers.
                If you need to sell inherited property, consider working with a{' '}
                <a
                  href="/elpaso-tx/cash-home-buyers-el-paso"
                  className="text-teal-600 hover:text-teal-700 font-semibold"
                >
                  cash home buyer in El Paso
                </a>{' '}
                who can work with alternative title solutions, or explore using
                an Affidavit of Heirship or formal probate instead.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                What if one heir refuses to sign the affidavit?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Unfortunately, if any heir entitled to inherit refuses to sign,
                you cannot proceed with a Small Estate Affidavit. All
                distributees must sign for the document to be valid. In this
                situation, you&apos;ll need to pursue formal probate instead.
                This is why family communication and agreement are essential
                before starting this process.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                How long does the Small Estate Affidavit process take?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                After the mandatory 30-day waiting period following death, the
                actual filing and approval process typically takes 2-4 weeks,
                depending on the court&apos;s schedule. This is significantly
                faster than formal probate, which usually takes 6-12 months or
                longer. However, gathering all necessary information and
                signatures before filing can add additional time.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Does a Small Estate Affidavit protect heirs from creditors?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                A Small Estate Affidavit provides less creditor protection than
                formal probate. While you must list known debts and ensure the
                estate is solvent, unknown creditors may still make claims
                against heirs after assets are distributed. Formal probate
                includes a creditor notification process that cuts off claims
                after a specific period, providing more protection.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Can I use this for{' '}
                <a
                  href="/sell-apartments"
                  className="text-teal-600 hover:text-teal-700 font-semibold"
                >
                  multiple family members own shares of an apartment building
                </a>
                ?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                No, a Small Estate Affidavit can only be used for the
                decedent&apos;s homestead property. If the deceased owned
                investment properties like apartment buildings, rental houses,
                or commercial real estate, you&apos;ll need to use formal
                probate or another legal method to transfer ownership. The
                homestead-only limitation is one of the most significant
                restrictions of this process.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Middle CTA Section */}
      <section className="py-20 bg-teal-600">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-xl p-8 md:p-12 shadow-2xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Need Help With Your Small Estate?
              </h2>
              <p className="text-xl text-gray-600">
                Get a free consultation to explore your options
              </p>
            </div>

            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-teal-600"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  Thank You!
                </h4>
                <p className="text-gray-600">
                  We&apos;ll contact you within 24 hours.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="small-estate-middle-form"
                className="space-y-4"
              >
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                  <input
                    type="text"
                    name="propertyAddress"
                    placeholder="Property Address (if applicable)"
                    value={formData.propertyAddress}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  />
                </div>
                <select
                  name="situation"
                  value={formData.situation}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                >
                  <option value="">Select Your Situation</option>
                  <option value="small-estate-affidavit">
                    Need Small Estate Affidavit
                  </option>
                  <option value="sell-inherited-property">
                    Want to Sell Inherited Property
                  </option>
                  <option value="estate-over-75k">Estate Over $75,000</option>
                  <option value="title-issues">Title or Legal Issues</option>
                  <option value="general-question">General Question</option>
                </select>
                <textarea
                  name="message"
                  placeholder="Tell us about your situation..."
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                ></textarea>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Get Free Consultation'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* How We Can Help Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=professional%20real%20estate%20investor%20meeting%20with%20family%20in%20comfortable%20living%20room%20discussing%20inherited%20property%20sale%20options%20warm%20welcoming%20atmosphere%20natural%20lighting%20showing%20trust%20and%20understanding%20professional%20photography&width=700&height=500&seq=small-estate-help-005&orientation=landscape"
                alt="Real Estate Investor Meeting with Family"
                className="w-full h-auto rounded-xl shadow-lg"
              />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                How <span className="text-teal-600">TREX RE LLC</span> Can Help
              </h2>

              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-6">
                  At TREX RE LLC, we understand that dealing with estate matters
                  and inherited property can be overwhelming, especially during
                  an already difficult time. Whether you&apos;re considering a
                  Small Estate Affidavit or exploring other options, we&apos;re
                  here to help you navigate the process.
                </p>

                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">
                        Buy Properties with Title Complications
                      </h4>
                      <p className="text-gray-600">
                        We can purchase inherited properties even when title
                        insurance is an issue, working with alternative title
                        solutions that traditional buyers can&apos;t.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">
                        Fast Cash Offers
                      </h4>
                      <p className="text-gray-600">
                        If you need to sell quickly to avoid ongoing expenses or
                        resolve estate matters, we can provide a fair cash offer
                        within 24 hours and close in as little as 7 days.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">
                        Handle Complex Situations
                      </h4>
                      <p className="text-gray-600">
                        Whether you&apos;re dealing with{' '}
                        <a
                          href="/title-issues"
                          className="text-teal-600 hover:text-teal-700 font-semibold"
                        >
                          complex title issues
                        </a>
                        , multiple heirs, or properties in disrepair, we have
                        the experience to find solutions.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-2xl text-teal-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">
                        No Repairs or Cleaning Required
                      </h4>
                      <p className="text-gray-600">
                        We buy properties in any condition, so you don&apos;t
                        have to spend time or money preparing the house for sale.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <a
                    href="tel:+1-915-206-5814"
                    className="inline-flex items-center px-6 py-4 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Call (915) 206-5814
                  </a>
                  <a
                    href="/get-cash-offer"
                    className="inline-flex items-center px-6 py-4 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Get Cash Offer
                    <i className="ri-arrow-right-line ml-2"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Resources Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Related <span className="text-teal-600">Resources</span>
            </h2>
            <p className="text-xl text-gray-600">
              Learn more about estate planning and inherited property in Texas
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <a
              href="/elpaso-tx/affidavit-of-heirship"
              className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow cursor-pointer group"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20estate%20planning%20workspace%20with%20affidavit%20of%20heirship%20documents%20family%20tree%20diagram%20notary%20seal%20and%20legal%20papers%20organized%20on%20desk%20warm%20office%20lighting%20professional%20photography&width=600&height=400&seq=small-estate-related-006&orientation=landscape"
                  alt="Affidavit of Heirship"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full mb-3">
                  Estate Documents
                </span>
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                  Affidavit of Heirship Guide
                </h3>
                <p className="text-gray-600 text-sm">
                  Learn about this alternative for transferring inherited real
                  estate without probate limitations.
                </p>
              </div>
            </a>

            <a
              href="/elpaso-tx/texas-probate-guide"
              className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow cursor-pointer group"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=texas%20probate%20court%20building%20with%20legal%20documents%20gavel%20and%20texas%20flag%20professional%20legal%20setting%20representing%20formal%20probate%20process%20natural%20lighting%20professional%20photography&width=600&height=400&seq=probate-guide-related&orientation=landscape"
                  alt="Texas Probate Guide"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full mb-3">
                  Legal Process
                </span>
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                  Complete Texas Probate Guide
                </h3>
                <p className="text-gray-600 text-sm">
                  Understand when formal probate is necessary and how the
                  process works in Texas.
                </p>
              </div>
            </a>

            <a
              href="/elpaso-tx/texas-intestacy-laws"
              className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow cursor-pointer group"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=texas%20legal%20books%20and%20inheritance%20law%20documents%20with%20family%20tree%20diagram%20showing%20intestate%20succession%20rules%20professional%20legal%20library%20setting%20natural%20lighting%20professional%20photography&width=600&height=400&seq=intestacy-related&orientation=landscape"
                  alt="Texas Intestacy Laws"
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <span className="inline-block px-3 py-1 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full mb-3">
                  Inheritance Law
                </span>
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                  Texas Intestacy Laws Explained
                </h3>
                <p className="text-gray-600 text-sm">
                  Discover how Texas law determines inheritance when someone
                  dies without a will.
                </p>
              </div>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SmallEstateAffidavitPage;